import { Add } from "@mui/icons-material";
import { AppBar, Button, Grid, Link, Paper, Tab, Tabs, useTheme } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  UrlPathCircle,
  UrlPathCircleCreate,
  UrlPathConfigValueCreate,
  UrlPathCustomField,
  UrlPathGdpr,
  UrlPathCardDesign,
  UrlPathCardDesignCreate,
  UrlPathReceiptDetail,
} from "../../api/url";
import {
  canMaintainCircles,
  canMaintainConfig,
  canMaintainGdpr,
  canMaintainCardDesigns,
  canReadCircles,
  canReadConfig,
  canReadGdpr,
  canReadCardDesigns,
} from "../../store/actions/LoginActions";
import MenuLayout from "../app/MenuLayout";
import CircleTable from "./circle/CircleTable";
import { customFieldProps, receiptDetailProps } from "./configValue/ConfigValueFormPage";
import ConfigValueTable from "./configValue/ConfigValueTable";
import Gdpr from "./gdpr/Gdpr";
import CardDesignTable from "./cardDesign/CardDesignTable";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

// Tab identities. Deliberately NOT translated: these double as the Tabs value, the switch
// discriminators below and the url mapping, so a copy change must never alter them.
const CIRCLE = "circle";
const CARD_DESIGN = "cardDesign";
const GDPR = "gdpr";

const Setup = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTab = getSelectedTab(location.pathname);
  const { t } = useTranslation();

  return (
    <MenuLayout headline={t("setup.pageTitle") + getTabLabel(selectedTab)}>
      {canMaintainTab(selectedTab) && (
        <Grid
          container
          justifyContent="flex-end"
          style={{
            padding: theme.spacing(2),
            marginTop: theme.spacing(2),
          }}
        >
          <Link to={getCreateUrlByTab(selectedTab)} component={RouterLink}>
            <Button variant="contained" color="primary">
              <Add />
              {t("setup.createButton", { tab: getTabLabel(selectedTab) })}
            </Button>
          </Link>
        </Grid>
      )}
      <Paper>
        <AppBar position="static">
          <Tabs value={selectedTab} onChange={(_event, newValue) => navigate(getUrlByTab(newValue))}>
            {canReadCircles() && <Tab label={getTabLabel(CIRCLE)} value={CIRCLE} />}
            {canReadCardDesigns() && <Tab label={getTabLabel(CARD_DESIGN)} value={CARD_DESIGN} />}
            {canReadGdpr() && <Tab label={getTabLabel(GDPR)} value={GDPR} />}
            {canReadConfig() && <Tab label={t("setup.filter")} value={receiptDetailProps.entityName} />}
            {canReadConfig() && <Tab label={t("setup.additionalFields")} value={customFieldProps.entityName} />}
          </Tabs>
        </AppBar>
        <Grid
          style={{
            padding: theme.spacing(4),
          }}
        >
          {getSelectedTable(selectedTab)}
        </Grid>
      </Paper>
    </MenuLayout>
  );
};

function getTabLabel(selectedTab: string) {
  switch (selectedTab) {
    case CIRCLE:
      return i18n.t("setup.circleLabel");
    case CARD_DESIGN:
      return i18n.t("setup.cardDesignLabel");
    case GDPR:
      return i18n.t("setup.gdprLabel");
  }
  return selectedTab;
}

function getSelectedTab(pathname: string) {
  switch (pathname) {
    case UrlPathCircle:
      return CIRCLE;
    case UrlPathCardDesign:
      return CARD_DESIGN;
    case UrlPathGdpr:
      return GDPR;
    case UrlPathReceiptDetail:
      return receiptDetailProps.entityName;
    case UrlPathCustomField:
      return customFieldProps.entityName;
  }
  return "";
}

function getSelectedTable(selectedTab: string) {
  switch (selectedTab) {
    case CIRCLE:
      return <CircleTable />;
    case CARD_DESIGN:
      return <CardDesignTable />;
    case GDPR:
      return <Gdpr />;
    case receiptDetailProps.entityName:
      return <ConfigValueTable {...receiptDetailProps} />;
    case customFieldProps.entityName:
      return <ConfigValueTable {...customFieldProps} />;
    default:
      return null;
  }
}

function canMaintainTab(selectedTab: string) {
  switch (selectedTab) {
    case CIRCLE:
      return canMaintainCircles();
    case CARD_DESIGN:
      return canMaintainCardDesigns();
    case GDPR:
      return canMaintainGdpr();
    case receiptDetailProps.entityName:
    case customFieldProps.entityName:
      return canMaintainConfig();
  }
  return false;
}

function getCreateUrlByTab(selectedTab: string) {
  switch (selectedTab) {
    case CIRCLE:
      return UrlPathCircleCreate;
    case CARD_DESIGN:
      return UrlPathCardDesignCreate;
    case receiptDetailProps.entityName:
      return UrlPathConfigValueCreate(UrlPathReceiptDetail);
    case customFieldProps.entityName:
      return UrlPathConfigValueCreate(UrlPathCustomField);
  }
  return "";
}

export function getUrlByTab(selectedTab: string) {
  switch (selectedTab) {
    case CIRCLE:
      return UrlPathCircle;
    case CARD_DESIGN:
      return UrlPathCardDesign;
    case GDPR:
      return UrlPathGdpr;
    case receiptDetailProps.entityName:
      return UrlPathReceiptDetail;
    case customFieldProps.entityName:
      return UrlPathCustomField;
  }
  return "";
}

export default Setup;
